import React, { useState } from "react"
import { useSelector } from "react-redux"
import { Box, Button, Typography } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import {
  getOrganizationsEnterpriseSelector,
} from "../../../../reducers/Organizations/organizations"
import DCCard from "./DCCard"
import AttachDCsModal from "./AttachDCsModal"
import EditDCParamsModal from "./EditDCParamsModal"
import DetachDCConfirmModal from "./DetachDCConfirmModal"

const sx = {
  section: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    bgcolor: "background.paper",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 3,
    py: 2,
  },
  title: {
    fontWeight: 500,
    fontSize: 18,
    color: "text.primary",
  },
  attachBtn: {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  emptyBody: {
    px: 3,
    pb: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  dashedBtn: {
    border: "1.5px dashed #CCCCCC",
    borderRadius: "8px",
    py: "32px",
    px: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "8px",
    bgcolor: "#fcfcfc",
    cursor: "pointer",
    transition: "all 150ms",
    "&:hover": {
      borderColor: "primary.main",
      bgcolor: "#E8F1FE",
      "& .dashed-icon": {
        borderColor: "primary.main",
        color: "primary.main",
      },
    },
  },
  dashedIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    bgcolor: "white",
    border: "1.5px solid #CCCCCC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#7C7C7C",
    transition: "all 150ms",
  },
  dashedTitle: {
    fontSize: 15,
    fontWeight: 500,
    color: "#262626",
    mt: "4px",
  },
  dashedSub: {
    fontSize: 13,
    color: "#7C7C7C",
    maxWidth: 380,
    lineHeight: 1.45,
  },
  alert: {
    display: "flex",
    gap: 2,
    alignItems: "center",
    px: 3,
    py: 2.5,
    bgcolor: "#FDEFDC",
    borderRadius: 2,
  },
  alertText: {
    fontSize: 16,
    lineHeight: 1.5,
    color: "text.primary",
  },
  listBody: {
    px: 3,
    pb: 3,
    marginTop: "16px",
  },
}

const WarningSvg = () => (
  <Box
    component="svg"
    sx={{ flexShrink: 0, width: 24, height: 24, display: "block" }}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M12 3.5L22 20.5H2L12 3.5Z" stroke="#F57C00" strokeWidth="2" strokeLinejoin="round" fill="none" />
    <line x1="12" y1="10" x2="12" y2="14.5" stroke="#F57C00" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="17.5" r="1" fill="#F57C00" />
  </Box>
)

const SitesLivraisonSection = () => {
  const enterprise = useSelector(getOrganizationsEnterpriseSelector)
  const [attachOpen, setAttachOpen] = useState(false)
  const [editingLink, setEditingLink] = useState(null)
  const [detachingLink, setDetachingLink] = useState(null)

  if (!enterprise) return null

  const distributionCenters = enterprise.distributionCenters || []
  const isEmpty = distributionCenters.length === 0

  return (
    <Box sx={sx.section}>
      <Box sx={sx.header}>
        <Typography sx={sx.title}>Sites de livraison</Typography>
        {!isEmpty && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            sx={sx.attachBtn}
            onClick={() => setAttachOpen(true)}
          >
            Rattacher un site
          </Button>
        )}
      </Box>

      {isEmpty ? (
        <Box sx={sx.emptyBody}>
          <Box
            sx={sx.dashedBtn}
            onClick={() => setAttachOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setAttachOpen(true)}
          >
            <Box sx={sx.dashedIcon} className="dashed-icon">
              <AddIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography sx={sx.dashedTitle}>Rattacher un site</Typography>
            <Typography sx={sx.dashedSub}>Sélectionnez parmi les sites existants</Typography>
          </Box>

          <Box sx={sx.alert}>
            <WarningSvg />
            <Typography sx={sx.alertText}>
              La commande ne pourra pas être envoyée en production sans site de livraison rattaché.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={sx.listBody}>
          {distributionCenters.map((link) => (
            <DCCard
              key={link.dc?.objectId}
              link={link}
              onEdit={setEditingLink}
              onDetach={setDetachingLink}
            />
          ))}
        </Box>
      )}

      <AttachDCsModal
        open={attachOpen}
        onClose={() => setAttachOpen(false)}
        enterpriseId={enterprise.objectId}
        alreadyAttachedIds={(enterprise.distributionCenters || []).map(l => l.dc?.objectId)}
      />

      {editingLink && (
        <EditDCParamsModal
          open={Boolean(editingLink)}
          onClose={() => setEditingLink(null)}
          enterpriseId={enterprise.objectId}
          link={editingLink}
        />
      )}

      {detachingLink && (
        <DetachDCConfirmModal
          open={Boolean(detachingLink)}
          onClose={() => setDetachingLink(null)}
          enterpriseId={enterprise.objectId}
          link={detachingLink}
        />
      )}
    </Box>
  )
}

export default SitesLivraisonSection
